import React, { useState, useEffect } from 'react';
import { IonAvatar, IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { camera } from 'ionicons/icons';
import { connect } from '../data/connect';
import { getDefaultAvatar } from '../utils/defaultAvatar';
import UserProfileService from '../services/UserProfileService';
import './UserAvatar.css';

interface UserAvatarProps {
  // Props del estado Redux
  userCed?: string;
  eventCode?: string;
  profilePhotoUrl?: string;
  isLoggedIn?: boolean;
  
  // Props de configuración
  size?: 'small' | 'medium' | 'large';
  editable?: boolean;
  showEditButton?: boolean;
  className?: string;
  
  // Props para casos especiales (speakers, etc.)
  fallbackUrl?: string;
  alt?: string;
  
  // Callbacks
  onPhotoClick?: () => void;
  onEditClick?: () => void;
  onPhotoUpdated?: (newPhotoUrl: string) => void;
}

interface StateProps {
  userCed?: string;
  eventCode?: string;
  profilePhotoUrl?: string;
  isLoggedIn?: boolean;
}

interface DispatchProps {
  setProfilePhotoUrl: (url?: string) => void;
}

type Props = UserAvatarProps & StateProps & DispatchProps;

/**
 * Componente unificado para mostrar avatares de usuario en toda la aplicación.
 * Maneja automáticamente la carga de fotos de perfil desde Firebase y 
 * proporciona fallbacks apropiados.
 */
const UserAvatar: React.FC<Props> = ({
  // Props del estado Redux
  userCed,
  eventCode,
  profilePhotoUrl: reduxProfilePhotoUrl,
  isLoggedIn,
  setProfilePhotoUrl,
  
  // Props de configuración
  size = 'medium',
  editable = false,
  showEditButton = false,
  className = '',
  
  // Props para casos especiales
  fallbackUrl,
  alt = 'Avatar de usuario',
  
  // Callbacks
  onPhotoClick,
  onEditClick,
  onPhotoUpdated
}) => {
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string>(getDefaultAvatar());
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Determinar la URL de la imagen a mostrar
  const getImageUrl = (): string => {
    // 1. Si hay error de imagen, usar default
    if (imageError) {
      return getDefaultAvatar();
    }

    // 2. Si hay una URL específica pasada como prop (para speakers, etc.)
    // Usar siempre el fallbackUrl cuando esté disponible, independientemente del estado de login
    if (fallbackUrl) {
      return fallbackUrl;
    }

    // 3. Si hay foto de perfil en Redux (solo para usuario logueado)
    if (isLoggedIn && reduxProfilePhotoUrl) {
      return reduxProfilePhotoUrl;
    }

    // 4. Si hay foto cargada localmente (solo para usuario logueado)
    if (isLoggedIn && currentPhotoUrl !== getDefaultAvatar()) {
      return currentPhotoUrl;
    }

    // 5. Fallback por defecto
    return getDefaultAvatar();
  };

  // Cargar foto de perfil del usuario logueado
  // SOLO si no se está usando fallbackUrl (para speakers, etc.)
  useEffect(() => {
    const loadUserPhoto = async () => {
      // No cargar foto del usuario si se está usando fallbackUrl (para speakers)
      if (!isLoggedIn || !userCed || !eventCode || reduxProfilePhotoUrl || fallbackUrl) {
        return;
      }

      setLoading(true);
      try {
        const userProfileService = UserProfileService.getInstance();
        const photoUrl = await userProfileService.getProfilePhotoUrl(userCed, eventCode);

        if (photoUrl && photoUrl !== getDefaultAvatar()) {
          setCurrentPhotoUrl(photoUrl);
          setProfilePhotoUrl(photoUrl);
        }
      } catch (error) {
        console.error('Error loading user photo:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserPhoto();
  }, [isLoggedIn, userCed, eventCode, reduxProfilePhotoUrl, setProfilePhotoUrl, fallbackUrl]);

  // Manejar error de carga de imagen
  const handleImageError = () => {
    setImageError(true);
  };

  // Manejar click en el avatar
  const handleAvatarClick = () => {
    if (onPhotoClick) {
      onPhotoClick();
    }
  };

  // Manejar click en botón de editar
  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
    }
  };

  // Obtener clase CSS según el tamaño
  const getSizeClass = (): string => {
    switch (size) {
      case 'small': return 'user-avatar-small';
      case 'large': return 'user-avatar-large';
      default: return 'user-avatar-medium';
    }
  };

  const displayUrl = getImageUrl();

  return (
    <div className={`user-avatar-container ${getSizeClass()} ${className}`}>
      <IonAvatar 
        className="user-avatar"
        onClick={handleAvatarClick}
        style={{ cursor: onPhotoClick ? 'pointer' : 'default' }}
      >
        <img
          src={displayUrl}
          alt={alt}
          onError={handleImageError}
        />
        {loading && (
          <div className="user-avatar-loading">
            <IonSpinner name="crescent" />
          </div>
        )}
      </IonAvatar>

      {showEditButton && editable && (
        <IonButton
          fill="clear"
          size="small"
          className="user-avatar-edit-button"
          onClick={handleEditClick}
          disabled={loading}
        >
          <IonIcon icon={camera} />
        </IonButton>
      )}
    </div>
  );
};

export default connect<UserAvatarProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    userCed: state.user.userced,
    eventCode: state.user.eventcode,
    profilePhotoUrl: state.user.profilePhotoUrl,
    isLoggedIn: state.user.isLoggedin
  }),
  mapDispatchToProps: {
    setProfilePhotoUrl: (url?: string) => ({ type: 'set-profile-photo-url', profilePhotoUrl: url })
  },
  component: UserAvatar
});
